import { getTransporterConfigurationIdentifier, ITransporterConfiguration } from '../models/transporter-configuration.model';
import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';

import { Observable } from 'rxjs';

import { map } from 'rxjs/operators';
import { TransporterConfigurationView } from '../models/transporter-configuration-view.model';
import { API_ENDPOINTS } from '../models/enums/endpoints.enum';
import { ApplicationConfigService } from './application-config.service';
import { ApiProviderService } from './api-provider.service';
import { createRequestOption, mapAsBody } from '../app/shared/util/request-util';
import { isPresent } from './operators';
import { TransporterConfigurationEnum } from '../models/enums/configuration.enum';

export type EntityResponseType = HttpResponse<ITransporterConfiguration>;
export type EntityArrayResponseType = HttpResponse<ITransporterConfiguration[]>;

@Injectable({ providedIn: 'root' })
export class TransporterConfigurationService {
  applicationConfigService = inject(ApplicationConfigService);
  public publicResourceUrl = API_ENDPOINTS.TRANSPORTER_CONFIGURATION;
  public moderatorResourceUrl = API_ENDPOINTS.MODERATOR_TRANSPORTER_CONFIGURATION;
  protected resourceUrl!: string;

  constructor(protected http: HttpClient, protected apiProvider: ApiProviderService) {
    this.resourceUrl = this.applicationConfigService.getEndpointFor('api/transporter-configurations');
  }

  create(transporterConfiguration: ITransporterConfiguration): Observable<EntityResponseType> {
    return this.http.post<ITransporterConfiguration>(this.resourceUrl, transporterConfiguration, { observe: 'response' });
  }

  update(transporterConfiguration: ITransporterConfiguration): Observable<EntityResponseType> {
    return this.http.put<ITransporterConfiguration>(`${this.resourceUrl}/${getTransporterConfigurationIdentifier(transporterConfiguration)}`, transporterConfiguration, {
      observe: 'response',
    });
  }

  partialUpdate(transporterConfiguration: ITransporterConfiguration): Observable<EntityResponseType> {
    return this.http.patch<ITransporterConfiguration>(`${this.resourceUrl}/${getTransporterConfigurationIdentifier(transporterConfiguration)}`, transporterConfiguration, {
      observe: 'response',
    });
  }

  find(id: number): Observable<EntityResponseType> {
    return this.http.get<ITransporterConfiguration>(`${this.resourceUrl}/${id}`, { observe: 'response' });
  }

  query(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http.get<ITransporterConfiguration[]>(this.resourceUrl, { params: options, observe: 'response' });
  }

  delete(id: number): Observable<HttpResponse<{}>> {
    return this.http.delete(`${this.resourceUrl}/${id}`, { observe: 'response' });
  }

  addTransporterConfigurationToCollectionIfMissing(
    transporterConfigurationCollection: ITransporterConfiguration[],
    ...transporterConfigurationsToCheck: (ITransporterConfiguration | null | undefined)[]
  ): ITransporterConfiguration[] {
    const transporterConfigurations: ITransporterConfiguration[] = transporterConfigurationsToCheck.filter(isPresent);
    if (transporterConfigurations.length > 0) {
      const transporterConfigurationCollectionIdentifiers = transporterConfigurationCollection.map(transporterConfigurationItem =>
        getTransporterConfigurationIdentifier(transporterConfigurationItem)
      );
      const transporterConfigurationsToAdd = transporterConfigurations.filter(transporterConfigurationItem => {
        const transporterConfigurationIdentifier = getTransporterConfigurationIdentifier(transporterConfigurationItem);
        if (transporterConfigurationIdentifier == null || transporterConfigurationCollectionIdentifiers.includes(transporterConfigurationIdentifier)) {
          return false;
        }
        transporterConfigurationCollectionIdentifiers.push(transporterConfigurationIdentifier);
        return true;
      });
      return [...transporterConfigurationsToAdd, ...transporterConfigurationCollection];
    }
    return transporterConfigurationCollection;
  }

  public getBooleanConfiguration(configType: TransporterConfigurationEnum, transporterId?: number): Observable<boolean | object> {
    return this.apiProvider.publicGet<boolean | object>(this.publicResourceUrl(`boolean/${configType}`, transporterId), { observe: 'response' }).pipe(mapAsBody);
  }

  public getStringConfiguration(configType: TransporterConfigurationEnum, transporterId?: number): Observable<string | object> {
    return this.apiProvider.publicGet<string | object>(this.publicResourceUrl(`string/${configType}`, transporterId), { observe: 'response' });
  }

  public getBooleanConfigsByTransporter(transporterId: number): Observable<TransporterConfigurationView[]> {
    return this.apiProvider.publicGet(`/transporter-configuration/${transporterId}`).pipe(map(val => (val.body as Array<any>).map((v: any) => v as TransporterConfigurationView)));
  }

  public getConfigsByEnum(transporterId: number, enums: TransporterConfigurationEnum[]): Observable<any> {
    return this.apiProvider.publicPost(`/transporter-configuration/${transporterId}`, enums).pipe(map(val => val.body));
  }

  public saveConfigurations(params: { key: string; value: string }[], transporterId: number): Observable<any> {
    return this.apiProvider.publicPost(`/transporter-configuration/${transporterId}/save`, params).pipe(map(val => val.body));
  }

  public removeConfiguration(key: string, transporterId: number): Observable<any> {
    return this.apiProvider.publicDelete(`/transporter-configuration/${transporterId}/remove/${key}`);
  }
}
